import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";
import { Reading, ReadingType } from "./readings";
import { Recording } from "./recordings";

// ============ TYPES ============

export interface MeetingVaad {
  id: string;
  name: string;
}

export interface MeetingMiddah {
  id: string;
  name: string;
}

export interface Chart {
  id: string;
  name: string;
  type: ReadingType;
  fileLink: string;
  globalContent: boolean;
  middahs: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string;
}

export type MeetingReading = Reading;
export type MeetingRecording = Recording;

export interface Meeting {
  id: string;
  name: string;
  date?: string;
  startTime: string;
  endTime: string;
  startTimeLocal?: string;
  endTimeLocal?: string;
  timezone?: string;
  timezoneLabel?: string;
  utcOffsetMinutes?: number;
  country?: string;
  state: string;
  city: string;
  address: string;
  zoomLink: string;
  vaad: MeetingVaad;
  middah: MeetingMiddah;
  readings: MeetingReading[];
  charts: Chart[];
  recordings: MeetingRecording[];
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingTableRow extends Meeting {
  key: string;
}

export interface MeetingTableData {
  data: MeetingTableRow[];
  total: number;
}

// ============ PARAMS ============

export interface MeetingCreateParams {
  name: string;
  // date: string;
  startTime: string;
  endTime: string;
  state: string;
  city: string;
  address: string;
  zoomLink: string;
  vaad: { id: string };
  importFromLibraryReadings?: { id: string }[];
  importFromLibraryRecordings?: { id: string }[];
}

export interface MeetingUpdateParams {
  name?: string;
  // date?: string;
  startTime?: string;
  endTime?: string;
  state?: string;
  city?: string;
  address?: string;
  zoomLink?: string;
  vaad?: { id: string };
  importFromLibraryReadings?: { id: string }[];
  importFromLibraryRecordings?: { id: string }[];
}

export interface MeetingGetParams
  extends
    PagingParams,
    Partial<
      Omit<Meeting, "id" | "vaad" | "middah" | "readings" | "recordings">
    > {
  orderByColumn?: keyof Omit<
    Meeting,
    "id" | "vaad" | "middah" | "readings" | "recordings"
  >;
  vaadId?: string;
  middahId?: string;
}

export const useMeetingGet = <DD = PagingDataResponse<Meeting>>(
  decorateData?: (data: PagingDataResponse<Meeting>) => DD,
): FetchGet<
  PagingDataResponse<Meeting>,
  MeetingGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("meetings", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useMeetingTableGet = () =>
  useMeetingGet<MeetingTableData>(
    ({ data, meta }: PagingDataResponse<Meeting>): MeetingTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export const useMeetingGetById = <DD = Meeting>(
  id?: string,
  decorateData?: (data: Meeting) => DD,
): FetchGetId<Meeting, DefaultFetchError, unknown, DD> =>
  useFetchGetId("meetings", id, {
    autoStart: !!id,
    decorateData,
  });

export const useMeetingCreate = (): FetchCreate<
  MeetingCreateParams,
  DefaultFetchError,
  MeetingCreateParams
> => useFetchCreate("meetings");

export const useMeetingUpdate = (
  id?: string,
): FetchUpdate<MeetingUpdateParams, DefaultFetchError, MeetingUpdateParams> =>
  useFetchUpdate("meetings", id);

export const useMeetingDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> =>
  useFetchDelete("meetings", id);

export const useMeetingExport = (): FetchGet<
  Blob,
  undefined,
  DefaultFetchError,
  Blob
> =>
  useFetchGet("meetings/export", {
    autoStart: false,
    startStateLoading: false,
    config: { responseType: "blob" },
  });
